import {
  getMoistureIndexSegments,
  MOISTURE_INDEX_DARK_RED,
  MOISTURE_INDEX_GREEN,
  MoistureIndexThresholds,
} from "../../utils/colorUtils/mapColorUtils";

interface ShadedChart {
  ctx: Pick<
    CanvasRenderingContext2D,
    | "save"
    | "restore"
    | "fillRect"
    | "fillStyle"
    | "globalAlpha"
    | "createLinearGradient"
    | "beginPath"
    | "moveTo"
    | "lineTo"
    | "stroke"
    | "strokeStyle"
    | "lineWidth"
    | "setLineDash"
    | "fillText"
    | "font"
    | "textAlign"
  >;
  chartArea: { left: number; right: number; top: number; bottom: number };
  scales: Record<
    string,
    { min: number; max: number; getPixelForValue: (value: number) => number }
  >;
}

export function createMoistureIndexShading(
  thresholds: MoistureIndexThresholds,
  axisId = "y-axis-0",
) {
  const segments = getMoistureIndexSegments(thresholds);

  return {
    id: "moisture-index-shading",
    beforeDraw(chart: ShadedChart) {
      const { ctx, chartArea, scales } = chart;
      const scale = scales[axisId];
      ctx.save();
      ctx.globalAlpha = 0.25;

      segments.forEach((segment, index) => {
        const from = index === 0 ? Math.min(segment.from, scale.min) : segment.from;
        const to =
          index === segments.length - 1 ? Math.max(segment.to, scale.max) : segment.to;
        const top = Math.max(chartArea.top, scale.getPixelForValue(to));
        const bottom = Math.min(chartArea.bottom, scale.getPixelForValue(from));
        if (bottom <= top) return;

        if (segment.gradientStartColor || segment.gradientEndColor) {
          const gradient = ctx.createLinearGradient(0, bottom, 0, top);
          gradient.addColorStop(0, segment.gradientStartColor ?? segment.color);
          gradient.addColorStop(0.55, segment.color);
          gradient.addColorStop(1, segment.gradientEndColor ?? segment.color);
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = segment.color;
        }
        ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, bottom - top);
      });

      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.font = "10px sans-serif";
      ctx.textAlign = "right";
      [
        { value: 0, color: MOISTURE_INDEX_DARK_RED, label: "PWP" },
        { value: 1, color: MOISTURE_INDEX_GREEN, label: "FC" },
      ].forEach(({ value, color, label }) => {
        const y = scale.getPixelForValue(value);
        if (y < chartArea.top || y > chartArea.bottom) return;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.fillStyle = "#333";
        ctx.fillText(label, chartArea.right - 3, y - 3);
      });

      ctx.restore();
    },
  };
}
